import ConfirmLinkHrisEmployeeModal from "@components/modals/ConfirmLinkHrisEmployeeModal";
import { useGetTraineesWithoutHrisEmployeeQuery } from "@components/modals/LinkHrisEmployeeModal.generated";
import Modal from "@components/modals/Modal";
import Avatar from "@components/ui/Avatar";
import SearchBar from "@components/ui/SearchBar";
import { useModal } from "@hooks/useModal";
import Spinner from "@src/deprecatedDesignSystem/components/Spinner";
import ToggleButton from "@src/deprecatedDesignSystem/components/ToggleButton";
import ButtonStyles from "@src/deprecatedDesignSystem/styles/ButtonStyles";
import { css, StyleSheet } from "aphrodite";
import Fuse from "fuse.js";
import gql from "graphql-tag";
import { FC, useMemo, useState } from "react";
import { HrisEmployeeFragment } from "../settings/hris/HrisEmployeeListContainer.generated";

const fuseConfig = {
  threshold: 0.4,
  keys: ["name", "phone_number"],
};

type Props = {
  hrisEmployee: HrisEmployeeFragment;
};

const LinkHrisEmployeeModal: FC<Props> = ({ hrisEmployee }) => {
  const { closeModal, showModal } = useModal();
  const { data, loading } = useGetTraineesWithoutHrisEmployeeQuery();
  const hrisEmployeeName = hrisEmployee.firstName + " " + hrisEmployee.lastName;
  const [search, setSearch] = useState(hrisEmployeeName);
  const [showLinkedEmployees, setShowLinkedEmployees] = useState(false);
  const filteredEmployees = useMemo(() => {
    if (!data?.Employees?.objects) return [];
    let _employees = data.Employees.objects;
    if (!showLinkedEmployees) {
      _employees = _employees.filter(
        (employee) => employee.hrisEmployee?.id === undefined,
      );
    }
    if (search) {
      return new Fuse(_employees, fuseConfig).search(search).map((e) => e.item);
    }
    return [..._employees].sort((a, b) => {
      if (a?.name && b?.name) {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });
  }, [data, search, showLinkedEmployees]);
  return (
    <Modal
      className={css(styles.modal)}
      title={`Link ${hrisEmployeeName}`}
      cancelButtonProps={{
        copy: "Cancel",
        onClick: () => {
          closeModal();
        },
      }}
    >
      <div className={css(styles.body)}>
        {loading ? (
          <div className={css(styles.spinnerContainer)}>
            <Spinner size={60} />
          </div>
        ) : (
          <div>
            <SearchBar
              className={css(styles.searchBar)}
              placeholder="Search for a trainee"
              value={search as string}
              onChange={setSearch}
            />
            <div style={{ display: "flex", marginLeft: 30 }}>
              <div
                style={{ marginRight: 12, fontSize: 12, alignItems: "center" }}
              >
                Show Opus users already linked to an HRIS user
              </div>
              <ToggleButton
                selected={showLinkedEmployees}
                onClick={() => setShowLinkedEmployees(!showLinkedEmployees)}
              />
            </div>
            <div className={css(styles.employeesContainer)}>
              {filteredEmployees.map((employee) => (
                <div key={employee.id} className={css(styles.employee)}>
                  <div className={css(styles.employeeName)}>
                    <Avatar
                      person={employee}
                      size={18}
                      style={{ marginRight: 8 }}
                    />
                    {employee.name}
                  </div>
                  <button
                    className={css(
                      ButtonStyles.buttonRounded,
                      ButtonStyles.buttonPrimary,
                    )}
                    onClick={() => {
                      showModal(
                        <ConfirmLinkHrisEmployeeModal
                          hrisEmployee={hrisEmployee}
                          opusEmployee={employee}
                        />,
                      );
                    }}
                  >
                    {employee.hrisEmployee?.id != null && `Override `}Link
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modal: {
    minWidth: 500,
  },
  body: {
    minHeight: 200,
  },
  employeesContainer: {
    margin: 20,
  },
  employee: {
    display: "flex",
    justifyContent: "space-between",
    margin: 10,
  },
  searchBar: {
    margin: 20,
  },
  employeeName: {
    display: "flex",
    alignItems: "center",
  },
  spinnerContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "20px",
  },
});

gql`
  query GetTraineesWithoutHrisEmployee {
    Employees: AdminEmployees {
      totalCount
      objects {
        ...EmployeeListItem
      }
    }
  }
`;

export default LinkHrisEmployeeModal;
